import useLogisticOperatorAutocomplete from '@/pages/Business/pages/LogisticOperator/hooks/useLogisticOperatorAutocomplete';
import useMonitoringDriversAutocomplete from '../hooks/useMonitoringDriversAutocomplete';
import { useCommercesAutocomplete } from '@/pages/Business/pages/Shippings/hooks';
import { useGetDriversQuery } from '@/pages/Business/pages/Drivers/services';
import type { DriverApi } from '@/pages/Business/pages/Drivers/models';
import type { Option } from '@/components/inputs/AutocompleteInput';
import { createContext, useContext } from 'react';
import useDebounce from '@/hooks/useDebounce';
import {
  DEBOUNCE_MS,
  MIN_SEARCH_LENGTH,
  DRIVERS_PAGE_SIZE,
} from '../constants/assignDriver.constants';

export interface AutocompleteOptionsResult {
  options: Option[];
  isLoading: boolean;
}

export interface DriversSearchParams {
  search: string;
  enabled: boolean;
  logisticOperatorId: string;
}

export interface DriversSearchResult {
  drivers: DriverApi[];
  isLoading: boolean;
  isError: boolean;
}

export interface MonitoringIntegrationContextValue {
  routeBuilderBasePath: string;
  shippingBasePath: string;
  useDriverOptions: (params: {
    search?: string;
    minLength?: number;
  }) => { driverOptions: Option[]; isDriversLoading: boolean };
  useLogisticOperatorOptions: (params: {
    search?: string;
    minLength?: number;
  }) => { logisticOperatorOptions: Option[]; isLogisticOperatorsLoading: boolean };
  useCommerceOptions: (params: {
    search?: string;
    minLength?: number;
  }) => { commerceOptions: Option[]; isCommercesLoading: boolean };
  useDriversSearch: (params: DriversSearchParams) => DriversSearchResult;
  allowCancelRoute?: boolean;
}

const useBusinessDriversSearch = ({
  search,
  enabled,
  logisticOperatorId,
}: DriversSearchParams): DriversSearchResult => {
  const debouncedSearch = useDebounce(search, DEBOUNCE_MS);
  const hasSearched = debouncedSearch.trim().length >= MIN_SEARCH_LENGTH;

  const {
    data: driversData,
    isLoading,
    isError,
  } = useGetDriversQuery(
    {
      paginated: { page: 1, pageSize: DRIVERS_PAGE_SIZE },
      filters: {
        assignableToRouteOperatorId: logisticOperatorId?.trim() || '',
        ...(hasSearched ? { search: debouncedSearch.trim() } : {}),
      },
    },
    { skip: !enabled || !logisticOperatorId?.trim() || !hasSearched }
  );

  return { drivers: driversData?.items ?? [], isLoading, isError };
};

const defaultValue: MonitoringIntegrationContextValue = {
  routeBuilderBasePath: '/business/shippings/routing',
  shippingBasePath: '/business/shipping',
  useDriverOptions: useMonitoringDriversAutocomplete,
  useLogisticOperatorOptions: useLogisticOperatorAutocomplete,
  useCommerceOptions: useCommercesAutocomplete,
  useDriversSearch: useBusinessDriversSearch,
};

export const MonitoringIntegrationContext =
  createContext<MonitoringIntegrationContextValue>(defaultValue);

export const useMonitoringIntegration = () =>
  useContext(MonitoringIntegrationContext);
